import React, { useCallback, useEffect, useState } from 'react';
import { Col, Row } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';

import KeyboardArrowLeft from '../../assets/svgComponents/KeyboardArrowLeft';
import KeyboardArrowRight from '../../assets/svgComponents/KeyboardArrowRight';
import KeyboardDoubleArrowLeft from '../../assets/svgComponents/KeyboardDoubleArrowLeft';
import KeyboardDoubleArrowRight from '../../assets/svgComponents/KeyboardDoubleArrowRight';
import { DEFAULT_PAGE, PER_PAGE_VALUES } from '../../constants/paginationDefaults';

import styles from './Pagination.module.scss';

const Pagination = ({ setPage, setPerPage, pageInfo, hidePaginationDetails = false }) => {
  const { t } = useTranslation('common');
  const page = pageInfo?.page || DEFAULT_PAGE;
  const pages = pageInfo?.pages || 0;

  const [currentPage, setCurrentPage] = useState(page);

  const setPaginationPage = useCallback(
    (value) => {
      setPage(value);
      setCurrentPage(value);
    },
    [setPage]
  );

  const handleClick = useCallback(
    (pageCategory) => {
      switch (pageCategory) {
        case 'first':
          if (page > 1) {
            setPaginationPage(1);
          }
          return;
        case 'last':
          if (pages > 1) {
            setPaginationPage(pages);
          }
          return;
        case 'prev':
          if (page > 1) {
            setPaginationPage(page - 1);
          }
          return;
        case 'next':
          if (page < pages) {
            setPaginationPage(page + 1);
          }
          return;

        default:
          setPaginationPage(1);
      }
    },
    [page, pages]
  );

  const handleInputChange = useCallback(
    (e) => {
      e.preventDefault();
      if (currentPage > 0 && currentPage <= pages) {
        setPage(currentPage);
      }
    },
    [currentPage, pages]
  );

  const handlePerPage = useCallback((perPageVal) => {
    setPerPage(perPageVal);
    setPage(1);
  }, []);

  useEffect(() => {
    setCurrentPage(page);
  }, [page]);

  return (
    <div className={`${styles.paginationFooter} text-center px-4 py-2`}>
      <Row>
        {!hidePaginationDetails && (
          <>
            <Col className="py-1" lg="5" md="6" sm="12">
              <Row className="text-center text-lg-start">
                <Col>
                  <Row className="align-items-center">
                    <Col>
                      <span className="fw-bold pr-1">{t('pagination.rowsPerPage')} </span>
                      <select
                        className={`${styles.rowsSelect} form-control-sm font-weight-bold`}
                        onChange={(e) => handlePerPage(+e.target.value)}
                        value={pageInfo?.items}
                      >
                        {PER_PAGE_VALUES.map((rowCount) => (
                          <option key={rowCount} value={rowCount}>
                            {rowCount}
                          </option>
                        ))}
                      </select>
                    </Col>
                  </Row>
                </Col>
              </Row>
            </Col>
            <Col className="py-1" lg="2" md="6" sm="12">
              {pageInfo?.from} - {pageInfo?.to} of {pageInfo?.count}
            </Col>
          </>
        )}
        <Col
          className={hidePaginationDetails ? 'py-1 text-center' : 'py-1 text-center text-lg-end'}
          lg={hidePaginationDetails ? '12' : '5'}
          sm="12"
        >
          <Row className="align-items-center">
            <Col>
              <button
                className={styles.pageBtn}
                type="button"
                disabled={page === 1}
                onClick={() => handleClick('first')}
              >
                <KeyboardDoubleArrowLeft />
              </button>
              <button
                className={styles.pageBtn}
                type="button"
                disabled={pageInfo?.prev === null}
                onClick={() => handleClick('prev')}
              >
                <KeyboardArrowLeft />
              </button>
              <span className="">
                {t('pagination.page')}&nbsp;
                <strong>
                  <span>
                    <form onSubmit={(e) => handleInputChange(e)}>
                      <input
                        type="number"
                        value={currentPage}
                        onChange={(e) => setCurrentPage(e.target.value)}
                        className={styles.paginationInput}
                        min="1"
                      />
                    </form>
                  </span>
                  &nbsp; {t('pagination.of')} {pageInfo?.pages}
                </strong>
                &nbsp;
              </span>

              <button
                type="button"
                className={styles.pageBtn}
                disabled={pageInfo?.next === null}
                onClick={() => handleClick('next')}
              >
                <KeyboardArrowRight />
              </button>
              <button
                className={styles.pageBtn}
                type="button"
                disabled={page === pages}
                onClick={() => handleClick('last')}
              >
                <KeyboardDoubleArrowRight />
              </button>
            </Col>
          </Row>
        </Col>
      </Row>
    </div>
  );
};

export default Pagination;
